import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PageHeader } from '../components/ui/Panel';
import Pill from '../components/ui/Pill';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { EmptyState, SkeletonRows, InfoNotice, ErrorNotice } from '../components/ui/States';
import { fmt, orDash } from '../lib/format';
import { rememberMember } from '../lib/lastMember';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 50;
const CHUNK = 80;

interface MemberRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  ibmCloudId: string | null;
  moodleUserId: number | null;
  profile: {
    geographicRegion: string | null;
    careerSeniority: string | null;
    sector: string | null;
  } | null;
}

interface MembersResponse {
  data: MemberRow[];
  pagination: { page: number; total: number; pages: number };
}

interface FilterOptions {
  clusters: string[];
  careerStages: string[];
  sectors: string[];
}

function LinkChip({ label, linked }: { label: string; linked: boolean }) {
  return (
    <span
      className={`font-semibold text-small leading-none px-[5px] py-[2px] border ${
        linked ? 'bg-ink border-ink text-paper' : 'bg-white border-line text-ink-3'
      }`}
      title={`${label} — ${linked ? 'linked' : 'not linked'}`}
    >
      {label}
    </span>
  );
}

const selectClass = 'border border-line bg-white px-3 py-[9px] text-table w-[168px] shrink-0';

interface ActiveResults {
  results: { userId: string; ok: boolean; error?: string }[];
  failed: number;
}

export default function Members() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const mutable = canMutate(user?.role);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [cluster, setCluster] = useState('');
  const [stage, setStage] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [mailOpen, setMailOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setSelected([]);
    setPage(1);
  }, [debounced, status, cluster, stage]);

  useEffect(() => {
    if (!mutable) setSelected([]);
  }, [mutable]);

  const { data: options } = useQuery<FilterOptions>('member-filter-options', async () => {
    const res = await api.get('/users/filter-options');
    return res.data.data;
  });

  const { data, isLoading, error } = useQuery<MembersResponse>(
    ['members', { debounced, status, cluster, stage, page }],
    async () => {
      const res = await api.get('/users', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debounced || undefined,
          isActive: status || undefined,
          cluster: cluster || undefined,
          careerStage: stage || undefined,
        },
      });
      return { data: res.data.data, pagination: res.data.pagination };
    },
    { keepPreviousData: true },
  );

  // Memoised so the fallback [] is not a fresh reference on every render, which would
  // defeat the useMemo below it.
  const rows = useMemo(() => data?.data ?? [], [data]);
  const total = data?.pagination.total ?? 0;
  const pages = data?.pagination.pages ?? 1;

  const selectedRows = useMemo(() => rows.filter((r) => selected.includes(r.id)), [rows, selected]);

  const setInactive = useMutation(
    async (ids: string[]) => {
      try {
        return (await api.post('/users/active', { userIds: ids, active: false })).data.data as ActiveResults;
      } catch (err) {
        const data = (err as { response?: { data?: { data?: ActiveResults } } }).response?.data?.data;
        if (data?.results) return data;
        throw err;
      }
    },
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries('members');
        if (result.failed === 0) {
          setSelected([]);
          setConfirmOpen(false);
        } else {
          setSelected(result.results.filter((r) => !r.ok).map((r) => r.userId));
        }
      },
    },
  );

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const allOnPage = rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const toggleAll = () =>
    setSelected((s) =>
      allOnPage ? s.filter((id) => !rows.some((r) => r.id === id)) : [...new Set([...s, ...rows.map((r) => r.id)])],
    );

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setCluster('');
    setStage('');
  };

  const openMember = (id: string) => {
    rememberMember(id);
    navigate(`/analytics/members/${id}`);
  };

  const emails = selectedRows
    .filter((r) => r.isActive)
    .map((r) => r.email)
    .filter(Boolean);
  const skippedInactive = selectedRows.filter((r) => !r.isActive).length;
  const chunks = Math.max(1, Math.ceil(emails.length / CHUNK));
  const mailto = `mailto:?bcc=${encodeURIComponent(
    emails.slice(0, CHUNK).join(','),
  )}&subject=${encodeURIComponent('DIVERT Mental Health')}`;

  const hasFilters = Boolean(debounced || status || cluster || stage);

  return (
    <>
      <PageHeader
        title="Members"
        sub="Search, filter, group and email cohorts."
        aside={
          !mutable ? (
            <InfoNotice>
              Read-only — your <strong>viewer</strong> role hides selection and every mutating control.
            </InfoNotice>
          ) : undefined
        }
      />

      {error ? <ErrorNotice>Could not load members. Retry in a moment.</ErrorNotice> : null}

      <Panel>
        <div className="flex flex-wrap items-center gap-[10px] px-5 py-[14px] border-b border-line-strong">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email"
            className="flex-1 min-w-[260px] border border-line-strong px-3 py-[9px] text-table rounded-sm min-h-[44px] placeholder:text-ink-3"
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectClass}>
            <option value="">Status: all</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select value={cluster} onChange={(e) => setCluster(e.target.value)} className={selectClass}>
            <option value="">Cluster: all</option>
            {(options?.clusters ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select value={stage} onChange={(e) => setStage(e.target.value)} className={selectClass}>
            <option value="">Career stage: all</option>
            {(options?.careerStages ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="font-semibold ml-auto text-small text-ink-2 tabular">
            {fmt(total)} members{mutable ? ` · ${fmt(selected.length)} selected` : ''}
          </span>
        </div>

        {isLoading ? (
          <SkeletonRows rows={8} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No members match"
            action={
              hasFilters ? (
                <Button variant="dark" onClick={clearFilters}>
                  Clear all filters
                </Button>
              ) : undefined
            }
          >
            Nothing matches this combination of search text and filters. Clear a filter, or widen the career stage.
          </EmptyState>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-paper border-b border-line">
                {mutable ? (
                  <th className="w-11 px-5 py-[10px]">
                    <input
                      type="checkbox"
                      checked={allOnPage}
                      onChange={toggleAll}
                      aria-label="Select all on page"
                      className="w-[15px] h-[15px] accent-accent"
                    />
                  </th>
                ) : null}
                {['Member', 'Cluster', 'Career stage', 'Sector', 'Status', 'Linked systems'].map((h, i, arr) => (
                  <th
                    key={h}
                    className={`font-semibold text-small text-ink-2 text-left py-[10px] ${
                      i === arr.length - 1 ? 'px-5' : 'px-[14px]'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isSel = selected.includes(r.id);
                return (
                  <tr
                    key={r.id}
                    onClick={() => openMember(r.id)}
                    className={`border-b border-line cursor-pointer ${isSel ? 'bg-selected' : 'hover:bg-selected'}`}
                  >
                    {mutable ? (
                      <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggle(r.id)}
                          aria-label={`Select ${r.firstName} ${r.lastName}`}
                          className="w-[15px] h-[15px] accent-accent"
                        />
                      </td>
                    ) : null}
                    <td className="px-[14px] py-3">
                      <div className="text-table font-semibold">
                        {r.firstName} {r.lastName}
                      </div>
                      <div className="text-small text-ink-2">{r.email}</div>
                    </td>
                    <td className="px-[14px] py-3 text-table">{orDash(r.profile?.geographicRegion)}</td>
                    <td className="px-[14px] py-3 text-table">{orDash(r.profile?.careerSeniority)}</td>
                    <td className="px-[14px] py-3 text-table">{orDash(r.profile?.sector)}</td>
                    <td className="px-[14px] py-3">
                      <Pill tone={r.isActive ? 'active' : 'inactive'}>{r.isActive ? 'Active' : 'Inactive'}</Pill>
                    </td>
                    <td className="px-5 py-3">
                      <span className="flex items-center gap-1">
                        <LinkChip label="IBM" linked={Boolean(r.ibmCloudId)} />
                        <LinkChip label="MDL" linked={Boolean(r.moodleUserId)} />
                        <LinkChip label="MTR" linked={false} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {rows.length > 0 ? (
          <div className="flex items-center justify-between px-5 py-[14px] border-t border-line-strong">
            <span className="font-semibold text-small text-ink-2 tabular">
              Page {page} of {pages} · {PAGE_SIZE} per page
            </span>
            <div className="flex gap-2">
              <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </Panel>

      {mutable && selected.length > 0 ? (
        <div
          className="fixed z-40 bg-surface border border-line rounded-md shadow-lift px-5 py-[14px] flex flex-wrap items-center justify-between gap-6"
          style={{ left: 16, right: 16, bottom: 16, maxWidth: 1200, marginInline: 'auto' }}
        >
          <div>
            <div className="font-semibold text-[15px] text-ink tabular">{fmt(selected.length)} members selected</div>
            <div className="text-small text-ink-3 mt-[2px]">
              {emails.length > CHUNK
                ? `Over ${CHUNK} recipients — will be chunked into ${chunks} drafts`
                : 'Recipients go in BCC · opens in your mail client'}
            </div>
          </div>
          <div className="flex items-center gap-[10px]">
            <Button variant="primary" onClick={() => setMailOpen(true)}>
              Email selected
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/groups', { state: { emails: selectedRows.map((r) => r.email) } })}
            >
              Add to group
            </Button>
            <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
              Set inactive
            </Button>
            <button
              onClick={() => setSelected([])}
              className="font-semibold text-small text-ink-3 hover:text-white px-2"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}

      {mailOpen ? (
        <Modal
          title="Email selected"
          onClose={() => setMailOpen(false)}
          footer={
            <>
              <a href={mailto} onClick={() => setMailOpen(false)}>
                <Button variant="primary">{chunks > 1 ? `Open draft 1 of ${chunks}` : 'Open mail client'}</Button>
              </a>
              <Button variant="ghost" onClick={() => setMailOpen(false)}>
                Cancel
              </Button>
            </>
          }
        >
          <p className="text-table">
            {emails.length > CHUNK
              ? `Draft 1 of ${chunks}. ${fmt(
                  emails.length,
                )} recipients exceed the ${CHUNK}-address limit, so the list is split across ${chunks} drafts. Send each in turn.`
              : `${fmt(emails.length)} recipients will be added to BCC in a single draft.`}
          </p>
          {skippedInactive > 0 ? (
            <p className="text-small text-ink-2 mt-2">
              {fmt(skippedInactive)} inactive {skippedInactive === 1 ? 'member is' : 'members are'} left out.
            </p>
          ) : null}
          <div className="bg-paper border border-line px-4 py-3 mt-4 text-small text-ink-2">
            Recipients go in <strong className="text-ink">BCC</strong>. Nothing is sent by the platform — this opens in
            your mail client.
          </div>
        </Modal>
      ) : null}

      {confirmOpen ? (
        <Modal
          title={`Set ${fmt(selected.length)} members to inactive?`}
          onClose={() => setConfirmOpen(false)}
          footer={
            <>
              <Button
                variant="destructive"
                disabled={setInactive.isLoading}
                onClick={() => setInactive.mutate(selected)}
              >
                {setInactive.isLoading ? 'Working…' : 'Set inactive'}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                Cancel
              </Button>
            </>
          }
        >
          <p className="text-table">
            Blocks each member’s IBM sign-in, which also locks them out of Moodle and the mentorship app. Nothing is
            deleted, and it can be reversed from the member’s page.
          </p>
          {setInactive.data && setInactive.data.failed > 0 ? (
            <div className="mt-4">
              <ErrorNotice>
                {fmt(setInactive.data.failed)} could not be deactivated and are still selected; nothing was changed for
                them:{' '}
                {setInactive.data.results
                  .filter((r) => !r.ok)
                  .map((r) => `${rows.find((m) => m.id === r.userId)?.email ?? r.userId} (${r.error})`)
                  .join('; ')}
              </ErrorNotice>
            </div>
          ) : null}
          {setInactive.isError ? (
            <div className="mt-4">
              <ErrorNotice>That did not go through. Retry, or check your permissions.</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
